import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';

import { Button, Modal } from '../ui';
import { apiClient } from '../../lib/api';
import { toastError } from '../../lib/toast-error';
import analytics from '../../lib/analytics';
import type { AccessMode, PaywallOrigin } from '../../hooks/useSubscriptionGate';

import { PAYWALL_BENEFITS, resolvePaywallCopy } from './paywall-copy';
import styles from './SubscriptionPaywall.module.css';

export interface SubscriptionPaywallProps {
  open: boolean;
  onClose: () => void;
  accessMode: AccessMode;
  origin?: PaywallOrigin;
}

/**
 * Paywall de assinatura (Fase 9) — PRÉ-correção.
 *
 * Aberto pelo `useSubscriptionGate` no clique de um recurso pago ou pelo 403
 * `SUBSCRIPTION_REQUIRED` do `apiClient`. Nenhum token de LLM foi gasto até
 * aqui: o gate do backend é anterior à chamada do modelo.
 *
 * Não tem relação com crédito: o paywall pós-correção do modelo antigo, que
 * revelava a nota consumindo 1 crédito, foi removido na Fase 9.
 */
export function SubscriptionPaywall({
  open,
  onClose,
  accessMode,
  origin,
}: SubscriptionPaywallProps) {
  const navigate = useNavigate();
  const [openingPortal, setOpeningPortal] = useState(false);
  const copy = resolvePaywallCopy(accessMode, origin);

  useEffect(() => {
    if (!open) return;
    analytics.track('result_paywall_shown', { origin, access_mode: accessMode });
  }, [open, origin, accessMode]);

  const goToPortal = useCallback(async () => {
    setOpeningPortal(true);
    try {
      const res = await apiClient('/billing/portal-session', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (!data.url) throw new Error('URL não retornada pela API');
      window.location.href = data.url;
    } catch (err) {
      toastError('Não foi possível abrir o portal de pagamento. Tente novamente.', {
        errorMessage: err instanceof Error ? err.message : undefined,
      });
      setOpeningPortal(false);
    }
  }, []);

  const handleCtaClick = useCallback(() => {
    analytics.track('result_paywall_cta_clicked', { origin, access_mode: accessMode });
    if (copy.ctaKind === 'portal') {
      void goToPortal();
      return;
    }
    onClose();
    navigate('/assinatura');
  }, [origin, accessMode, copy.ctaKind, goToPortal, onClose, navigate]);

  return (
    <Modal isOpen={open} onClose={onClose} title={copy.title} size="md">
      <div className={styles.wrapper}>
        <p className={styles.lead}>
          <Lock size={16} aria-hidden className={styles.leadIcon} />
          <span>{copy.description}</span>
        </p>

        <ul className={styles.benefits}>
          {PAYWALL_BENEFITS.map((benefit) => (
            <li key={benefit} className={styles.benefit}>
              <Check size={16} aria-hidden className={styles.benefitIcon} />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        <div className={styles.actions}>
          <Button
            variant="primary"
            size="md"
            onClick={handleCtaClick}
            loading={openingPortal}
            disabled={openingPortal}
          >
            {copy.ctaLabel}
          </Button>
          <Button variant="ghost" size="md" onClick={onClose} disabled={openingPortal}>
            Agora não
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SubscriptionPaywall;
